import { REGIONS_WITH_COUNTRIES } from "@/constants/dropdownOptions";

/**
 * Bidirectional region/country synchronization helpers.
 *
 * Rules enforced:
 *  - Selecting a region adds all of its countries.
 *  - Deselecting a region removes all of its countries.
 *  - Selecting a country auto-selects the region it belongs to.
 *  - Deselecting a country auto-deselects its region (you can't have
 *    a region selected without at least one of its countries).
 *
 * This prevents inconsistent states such as "Western Europe" + "Afghanistan".
 */

// Build reverse lookup: country -> region (one country may belong to
// multiple regions in the data; pick all).
const COUNTRY_TO_REGIONS: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  for (const [region, countries] of Object.entries(REGIONS_WITH_COUNTRIES)) {
    for (const country of countries) {
      if (!map[country]) map[country] = [];
      map[country].push(region);
    }
  }
  return map;
})();

export function getRegionsForCountry(country: string): string[] {
  return COUNTRY_TO_REGIONS[country] || [];
}

export function getCountriesForRegion(region: string): string[] {
  return REGIONS_WITH_COUNTRIES[region] || [];
}

export interface RegionCountryState {
  regions: string[];
  countries: string[];
}

/**
 * Toggle a region. Adds/removes all of its countries.
 */
export function toggleRegionSync(
  state: RegionCountryState,
  region: string,
): RegionCountryState {
  const regionCountries = getCountriesForRegion(region);
  const isAdding = !state.regions.includes(region);

  const regions = isAdding
    ? [...state.regions, region]
    : state.regions.filter((r) => r !== region);

  const countries = isAdding
    ? Array.from(new Set([...state.countries, ...regionCountries]))
    : state.countries.filter((c) => !regionCountries.includes(c));

  return { regions, countries };
}

/**
 * Toggle a country. Adds the country's region(s) when adding;
 * removes any region whose countries are no longer all/any selected
 * after removal.
 */
export function toggleCountrySync(
  state: RegionCountryState,
  country: string,
): RegionCountryState {
  const isAdding = !state.countries.includes(country);
  const countryRegions = getRegionsForCountry(country);

  const countries = isAdding
    ? [...state.countries, country]
    : state.countries.filter((c) => c !== country);

  let regions = state.regions;

  if (isAdding) {
    // Add any region this country belongs to that isn't already selected.
    const toAdd = countryRegions.filter((r) => !regions.includes(r));
    if (toAdd.length) regions = [...regions, ...toAdd];
  } else {
    // Remove regions whose countries are no longer represented at all.
    regions = regions.filter((r) => {
      const regionCountries = getCountriesForRegion(r);
      // Keep region only if at least one of its countries is still selected.
      return regionCountries.some((c) => countries.includes(c));
    });
  }

  return { regions, countries };
}
